import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const titleOverrides = new Map([
  ['12-rolls-triple-363-sheet-2-ply-toilet-tissue', 'Cashmere Toilet Paper'],
  ['3-ply-ultra-soft-facial-tissues-12-flat-boxes-82-sheets-per-box-carbon-neutral', 'Royale Facial Tissues'],
  ['vel-our-2-ply-toilet-paper-30-rolls-147-sheets-per-roll-4-410-total-septic-safe-carbon-neutral', 'Royale Velour Toilet Paper'],
  ['royale-dinner-napkins-3-ply-75-paper-napkins-63435-73017-5', 'Royale Dinner Napkins'],
  ['in-wash-laundry-scent-booster-moonlight-breeze-scent-680-g-fresh-extender-odour-protection', 'Gain Laundry Scent Booster'],
  ['downy-ultra-soft-fabric-softener-liquid-calm-lavender-and-vanilla-bean-1-66-l-pack-of-2', 'Downy Ultra Soft Fabric Softener'],
]);

const conciseNames = [
  [/^Downy Free & Gentle Liquid Fabric Softener.*/i, 'Downy Free & Gentle Fabric Softener'],
  [/^Downy Rinse & Refresh Free & Gentle.*/i, 'Downy Rinse & Refresh Free & Gentle'],
  [/^Downy Ultra Laundry Liquid Fabric Softener.*/i, 'Downy Ultra Fabric Softener'],
  [/^Tide Power PODS 2-in-1 Laundry Detergent Pods with Downy.*/i, 'Tide Power PODS with Downy'],
  [/^Pronamel Children.*Toothpaste.*/i, "Pronamel Children's Toothpaste"],
  [/^Shout Advanced Laundry Stain Remover Gel.*/i, 'Shout Advanced Stain Remover Gel'],
  [/^Oral-B Pro 300.*/i, 'Oral-B Pro 300 Electric Toothbrush'],
  [/^Oral-B Glide Complete.*Floss Picks.*/i, 'Oral-B Glide Mint Floss Picks'],
  [/^Colgate Advanced 360.*Toothbrush.*/i, 'Colgate 360 Soft Toothbrush'],
  [/^Palmolive Essential Clean Dish Soap.*/i, 'Palmolive Essential Clean Dish Soap'],
  [/^Vileda Scrunge Multi-Use.*/i, 'Vileda Scrunge Multi-Use Sponge'],
  [/^Vileda Scrunge Heavy Duty.*/i, 'Vileda Scrunge Heavy Duty Sponge'],
  [/^Aquasonic Black Series.*/i, 'Aquasonic Black Series Toothbrush'],
];

const normalizeSpec = value => value
  .replace(/^1\s+count\s*\(Pack of (\d+)\)$/i, '$1 Count')
  .replace(/\s*\(Pack of (\d+)\)/gi, (_, count) => Number(count) === 1 ? '' : ` x ${count}`)
  .replace(/\bml\b/gi, 'mL')
  .replace(/\b(?:l|liters?|litres?)\b/gi, 'L')
  .replace(/\bcount\b/gi, 'Count')
  .replace(/(\d)(mL|L|kg|g)\b/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim();

function titleSpec(title) {
  title = title.replace(/(\d)-(kg|g|mL)\b/gi, '$1 $2');
  const amount = title.match(/\b\d+(?:\.\d+)?\s*(?:mL|litres?|liters?|L|kg|g|fl\s*oz|oz)\b/i)?.[0];
  const equivalentRolls = title.match(/\b(\d+)\s*(?:Equals?|=)\s*\d+\s*Rolls\b/i);
  const pack = title.match(/\bPack of (\d+)\b/i);
  const count = equivalentRolls ? `${equivalentRolls[1]} Rolls` : pack ? `${pack[1]} Pack` : title.match(/\b\d+\s*(?:(?:Mega|Triple|Double|Jumbo|Flat|Tissue|Trash|Paper)\s+)?(?:Rolls|Boxes|Bags|Sheets|Treatments|Count|ct|Pack|Brush Heads|Paper Napkins)\b/i)?.[0];
  return [amount, count].filter(Boolean).map(normalizeSpec).join(' / ');
}

export function formatMobileProductTitle({ baseTitle, fullTitle, handle, optionNames = [], optionValues = [] }) {
  let base = titleOverrides.get(handle) || baseTitle.split(/\s+\|\s+/)[0].trim();
  for (const [pattern, replacement] of conciseNames) base = base.replace(pattern, replacement);
  base = base.replace(/\s*\(Pack of \d+\)/gi, '').replace(/\b(\d+) Pack (?=Natural Sponges)/i, '').trim();

  const options = optionNames.map((name, index) => ({ name, value: optionValues[index] || '' }));
  const size = options.find(option => /^(?:size|volume|weight|count|quantity|item parcel quantity)$/i.test(option.name));
  let spec = '';
  if (size?.value && size.value !== 'Default Title') {
    spec = /^\d+$/.test(size.value) ? `${size.value} Pack` : normalizeSpec(size.value);
  } else {
    const style = options.find(option => /style/i.test(option.name));
    if (/^\d+\s+Mega$/i.test(style?.value || '')) {
      spec = `${style.value} Rolls`;
    } else if (style?.value && /\d/.test(style.value) && !/iO\d/i.test(style.value)) {
      spec = titleSpec(style.value) || normalizeSpec(style.value);
    } else {
      spec = titleSpec(fullTitle);
    }
  }
  if (spec && base.toLowerCase().includes(spec.toLowerCase())) spec = '';
  return spec ? `${base}, ${spec}` : base;
}

export class MobileProductTitle extends Component {
  #section;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selection += 1;
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #render() {
    const compact = this.querySelector('.mobile-product-title__compact');
    if (!compact) return;
    compact.textContent = formatMobileProductTitle({
      baseTitle: this.dataset.baseTitle,
      fullTitle: this.dataset.fullTitle,
      handle: this.dataset.handle,
      optionNames: JSON.parse(this.dataset.optionNames || '[]'),
      optionValues: JSON.parse(this.dataset.optionValues || '[]'),
    });
  }

  #handleProductSelect = event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const selection = ++this.#selection;
    event.promise.then(({ detail }) => {
      if (!this.isConnected || selection !== this.#selection || !detail?.html) return;
      if (!detail.newProduct && detail.productId && detail.productId !== this.dataset.productId) return;
      const source = detail.html.querySelector(`mobile-product-title[data-block-id="${CSS.escape(this.dataset.blockId)}"]`);
      if (!source) return;
      for (const name of ['productId', 'handle', 'fullTitle', 'baseTitle', 'optionNames', 'optionValues']) {
        this.dataset[name] = source.dataset[name];
      }
      const full = this.querySelector('.mobile-product-title__full');
      if (full) full.textContent = this.dataset.fullTitle;
      this.#render();
    }).catch(error => {
      if (error?.name !== 'AbortError') console.warn('[mobile-product-title] Variant update failed:', error);
    });
  };
}

if (!customElements.get('mobile-product-title')) {
  customElements.define('mobile-product-title', MobileProductTitle);
}
